import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Coins } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { http } from "@/lib/api";

export const CreditsBar = ({ refreshKey, onData }) => {
  const [c, setC] = useState(null);
  const [open, setOpen] = useState(false);
  const [params, setParams] = useSearchParams();
  const load = () => http.get("/credits").then((r) => { setC(r.data); onData?.(r.data); });
  useEffect(() => { load(); }, [refreshKey]);
  useEffect(() => {
    const sid = params.get("credit_session");
    if (!sid) return;
    let n = 0;
    const poll = async () => {
      const r = (await http.get(`/credits/status/${sid}`)).data;
      if (r.payment_status === "paid") { toast.success(`${r.credits} kreditter lagt til`); load(); setParams({}); }
      else if (n++ < 10) setTimeout(poll, 2000);
    };
    poll();
  }, [params]); // eslint-disable-line react-hooks/exhaustive-deps
  const buy = async (id) => {
    const r = await http.post("/credits/checkout", { package_id: id, origin_url: window.location.origin });
    window.location.href = r.data.url;
  };
  if (!c) return null;
  return (
    <>
      <button data-testid="credits-balance" onClick={() => setOpen(true)} className="flex items-center gap-1.5 rounded-lg border border-border bg-secondary px-3 py-1.5 text-xs font-semibold">
        <Coins className="h-4 w-4 text-primary" />
        {c.unlimited ? "Ubegrenset (eier) · Pro" : `${c.credits} kreditter · ${c.free_searches_left} gratis søk · ${c.pro ? "Pro" : "Standard"}`}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card" data-testid="credits-dialog">
          <DialogHeader><DialogTitle className="font-heading">Kreditter</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">1 kreditt = 1 søk (Standard opptil {c.standard_limit} bedrifter, Pro opptil {c.pro_limit}). Du har {c.free_searches_left} gratis søk igjen (maks {c.free_limit} bedrifter). Brukt totalt: {c.searches_used} søk. Søk uten treff er gratis. Engangskjøp – ingen abonnement. 300, 600 og 1200 kreditter låser opp Pro.</p>
          {c.test_mode && <p className="text-xs font-semibold text-amber-700">Betaling i testmodus – bruk testkort 4242 4242 4242 4242.</p>}
          <div className="grid gap-2 grid-cols-2">
            {c.packages.map((p) => (
              <Button key={p.id} data-testid={`buy-${p.id}`} variant={p.id === "p300" ? "default" : "outline"} className="relative h-auto flex-col py-3" onClick={() => buy(p.id)}>
                {p.badge && <span className="absolute -top-2 right-2 rounded-full bg-amber-300 px-2 text-[10px] font-bold text-stone-900">{p.badge}</span>}
                <span className="text-lg font-extrabold">{p.credits} søk</span>
                <span className="text-xs">{p.price} kr · {(p.price / p.credits).toFixed(2).replace(".", ",")} kr/søk{p.unlocks_pro ? " · Pro" : ""}</span>
              </Button>
            ))}
          </div>
          {c.history.length > 0 && (
            <div className="max-h-40 overflow-y-auto text-xs divide-y divide-border">
              {c.history.map((h, i) => <div key={i} className="flex justify-between py-1"><span>{h.at.slice(0, 16).replace("T", " ")} · {h.type === "purchase" ? "Kjøp" : h.type === "free_search" ? `Gratis søk (${h.prospects} treff)` : h.type === "admin_grant" ? "Tildelt" : `Søk ${h.tier || ""} (${h.prospects} treff)`}</span><b>{h.amount > 0 ? `+${h.amount}` : h.amount || ""}</b></div>)}
            </div>
          )}
          <p className="text-[11px] text-muted-foreground">Husk markedsføringsloven § 15: send bare relevante henvendelser til bedriftsadresser, ikke til privatpersoner uten samtykke, og gi alltid mulighet til å reservere seg. Ingen e-post sendes uten at du har godkjent mottaker og innhold.</p>
        </DialogContent>
      </Dialog>
    </>
  );
};
